import { gql, useQuery } from '@apollo/client';
import { getUserCountryState } from './__generated__/getUserCountryState';

export const USER_LOCATION_QUERY = gql`
  query getUserCountryState {
    user {
      id
      country {
        value
      }
      state {
        value
      }
    }
  }
`;

export const CANADA = 'Canada';
const USA = 'United States of America';
export const UAE = 'United Arab Emirates';
const COUNTRIES_REQUIRING_STATES = [CANADA, USA, UAE];

type CountriesRequiringStatesType = typeof CANADA | typeof USA | typeof UAE;

// A type guard for narrowing the provided country to the CountriesRequiringStatesType where appropriate
export function countryRequiresState(
  country: string,
): country is CountriesRequiringStatesType {
  return COUNTRIES_REQUIRING_STATES.includes(country);
}

/**
 * Provides the user's place of residence data.
 * For convenience, also provides booleans indicating whether their location data is valid
 * and whether their country of residence requires a state.
 */
export function useUserLocation() {
  const {
    data: userData,
    loading,
    error,
  } = useQuery<getUserCountryState>(USER_LOCATION_QUERY);

  const requiresState = Boolean(
    userData?.user?.country?.value &&
    countryRequiresState(userData.user.country.value),
  );

  const userHasValidLocationData = () => {
    if (
      !userData?.user?.country?.value ||
      userData?.user?.country?.value === '0'
    )
      return false;

    if (requiresState) {
      return Boolean(userData?.user?.state?.value);
    }

    return Boolean(userData.user.country.value);
  };

  return {
    userLocationData: userData,
    hasValidLocationData: userHasValidLocationData(),
    requiresState,
    loading,
    error,
  };
}
